import React from 'react';
import { motion } from 'framer-motion';
import { socialProof } from '@/data/content';
import Button from '@/components/ui/Button';

const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const },
};

const iconMap = {
    google: (
        <svg className="w-6 h-6 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
        </svg>
    ),
    facebook: (
        <svg className="w-6 h-6 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
        </svg>
    ),
    chatgpt: (
        <svg
            className="w-6 h-6 text-gray-900 dark:text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 3.2a4 4 0 0 1 3.46 2l2.01-.02a4 4 0 0 1 3.45 6l-1.02 1.73 1 1.73a4 4 0 0 1-3.47 6h-1.98A4 4 0 0 1 8.55 20H6.57a4 4 0 0 1-3.47-6l1-1.73-1.02-1.73a4 4 0 0 1 3.45-6l2.01.02A4 4 0 0 1 12 3.2Z" />
            <path d="M8.5 6.5 15.8 10.7" />
            <path d="M8.2 17.4V9.1" />
            <path d="m15.5 17.5-7.2-4.2" />
            <path d="m15.8 6.6-7.3 4.2" />
            <path d="m18 14.8-7.3 4.2" />
        </svg>
    ),
    gemini: (
        <svg className="w-6 h-6 text-[#5B7CFF]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l1.78 5.12L19 9l-5.22 1.88L12 16l-1.78-5.12L5 9l5.22-1.88L12 2Z" />
            <path d="M18 14l.98 2.82L22 18l-3.02 1.18L18 22l-.98-2.82L14 18l3.02-1.18L18 14Z" />
            <path d="M6 14l.98 2.82L10 18l-3.02 1.18L6 22l-.98-2.82L2 18l3.02-1.18L6 14Z" />
        </svg>
    ),
};

const SocialProofReviewsBlock: React.FC = () => {
    return (
        <motion.section {...fadeInUp} id="avis" className="py-20 md:py-32 bg-gray-50 dark:bg-gray-900/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-6 leading-tight">
                        {socialProof.heading}
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-400 font-medium leading-relaxed">
                        {socialProof.description}
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
                    <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 md:p-8">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
                            {socialProof.human_heading}
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-4">
                            {socialProof.human_links.map((link) => (
                                <Button
                                    key={link.label}
                                    variant="secondary"
                                    size="lg"
                                    className="w-full sm:w-auto"
                                    href={link.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    icon={iconMap[link.icon]}
                                >
                                    {link.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 md:p-8">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
                            {socialProof.ai_heading}
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-4">
                            {socialProof.ai_links.map((link) => (
                                <Button
                                    key={link.label}
                                    variant="secondary"
                                    size="lg"
                                    className="w-full sm:w-auto"
                                    href={link.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    icon={iconMap[link.icon]}
                                >
                                    {link.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};

export default SocialProofReviewsBlock;
